/* eslint-disable max-lines */
import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import {
    ChangeRequestSubType,
    MemberEscalationSubType,
    MemberInquiryEscalationOptionTree,
    RequestChatIcon,
    RequestType,
    RequestTypeDisplayTextMap,
    UserCell,
} from '@strata/react-chat';
import {
    type ColumnDef,
    type FilterCategory,
    GridWithFilters,
    PrimaryData,
    SecondaryData,
    TimeFrameOptions,
} from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { Icon } from '@strata/react-status-icons';
import { PayerRequestStatusIndicator } from '@strata/react-status-indicators';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';

import { ViewsControls } from 'components/shared/ViewsControls';
import { PayerRequestNumber } from 'components/treatments/PayerRequestNumber';
import { type TimeFrameOption } from 'services/interfaces';
import {
    getActionButtonPageTitleLabel,
    ReadPermissionRequestTypeMap,
} from 'services/payerRequests';
import { AppRoutes } from 'services/routes';
import { useAppSelector } from 'store';
import { useUserNameMap } from 'store/api/authApi';
import {
    type ArchivedRequestSummary,
    useGetArchivedRequestsQuery,
} from 'store/api/payerRequestApi';
import { useGridState } from 'store/grids/gridsThunks';

const initialSortProperties = [{ id: 'completed_date', desc: true }];

const typeLevel2Keys = () =>
    MemberInquiryEscalationOptionTree.flatMap(
        // The unknown is required by eslint -- it would run well without it.
        (item) =>
            item.typeLevel2 as unknown as { name: string; secondaryData: string | undefined }[],
    ).map((item) => item.name);

const timeFrameConfig = {
    key: 'completed_date',
    title: 'Request Time Frame',
    defaultValue: TimeFrameOptions.PAST_WEEK,
    optionOverrideMap: {
        [TimeFrameOptions.PAST_DAY]: { text: 'Today' },
        [TimeFrameOptions.PAST_WEEK]: { text: 'Last 7 Days' },
        [TimeFrameOptions.PAST_MONTH]: { text: 'Last 30 Days' },
        [TimeFrameOptions.PAST_QUARTER]: { text: 'Last 3 Months' },
        [TimeFrameOptions.PAST_YEAR]: { text: 'Last 12 Months' },
        ALL_TIME: { text: 'All', handler: () => true },
    },
};

interface RequestArchiveGridProps {
    pbmOnlyAccess?: boolean;
}

const filterCategories = (
    permissions: string[],
    pbmOnlyAccess: boolean,
): FilterCategory<ArchivedRequestSummary>[] => {
    const categories: FilterCategory<ArchivedRequestSummary>[] = [
        {
            key: 'request_type',
            title: 'Request Category',
            customValues: Object.entries(ReadPermissionRequestTypeMap)
                // Only show requestTypes user has permissions for
                .filter(([permission]) => permissions.includes(permission))
                .map(([_, value]) => ({
                    value,
                    text: RequestTypeDisplayTextMap[value],
                })),
        },
        {
            key: 'request_sub_type',
            title: 'Request Type',
            customValues: [
                ...new Set([
                    ...Object.values(ChangeRequestSubType),
                    ...Object.values(MemberEscalationSubType),
                ]),
            ]
                .sort(sortAsc)
                .map((value) => ({ value, text: value })),
        },
        {
            key: 'type_level_2',
            title: 'Request Subtype',
            customValues: [...new Set(typeLevel2Keys())]
                .sort(sortAsc)
                .map((value) => ({ value, text: value })),
        },
        {
            key: 'request_status',
            title: 'Status',
            customValues: [
                PayerRequestStatusIndicator.Statuses.COMPLETED,
                PayerRequestStatusIndicator.Statuses.CANCELED,
            ].map((value) => ({ value, text: PayerRequestStatusIndicator.StatusMessages[value] })),
        },
        {
            key: 'urgent',
            title: 'Request Urgency',
            customValues: [{ value: 'true', text: 'Urgent' }],
        },
    ];

    if (pbmOnlyAccess) {
        categories.push({
            key: 'pbm_only',
            title: 'Visibility',
            customValues: [{ value: 'true', text: 'PBM Only' }],
        });
    }

    return categories;
};

const columns: ColumnDef<ArchivedRequestSummary>[] = [
    {
        accessorKey: 'request_type',
        header: 'Request Category',
        size: 200,
        cell: ({ getValue }) => <PrimaryData data={RequestTypeDisplayTextMap[getValue()]} />,
    },
    {
        accessorKey: 'member_id',
        header: 'Member ID',
        size: 120,
    },
    {
        accessorKey: 'request_sub_type',
        header: 'Request Type',
        size: 150,
    },
    {
        accessorKey: 'type_level_2',
        header: 'Request Subtype',
        size: 150,
    },
    {
        accessorKey: 'summary',
        header: 'Summary',
        size: 150,
    },
    {
        accessorKey: 'submitter_upn',
        header: 'Created By',
        size: 150,
        cell: ({ getValue, row }) => (
            <UserCell upn={getValue()} fullName={row.original.submitter_full_name} small />
        ),
    },
    {
        accessorKey: 'resolved_at',
        header: 'Resolved Date',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'completed_date',
        header: 'Closed On',
        size: 160,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'is_unread',
        header: () => <Icon icon={IconNames.CHAT} />,
        enableSorting: false,
        size: 50,
        cell: ({ getValue }) => <RequestChatIcon isUnread={getValue()} />,
    },
    {
        accessorKey: 'urgent',
        header: () => <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} />,
        enableSorting: false,
        size: 50,
        cell: ({ getValue }) =>
            getValue() ? (
                <Icon icon={IconNames.WARNING_SIGN} intent={Icon.Intent.WARNING} title="Urgent" />
            ) : null,
    },
    {
        accessorKey: 'request_number',
        header: 'Request #',
        size: 90,
        cell: ({ getValue, row }) => (
            <PayerRequestNumber text={getValue()} pbmOnly={row.original.pbm_only} />
        ),
    },
    {
        accessorKey: 'request_status',
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => (
            <PayerRequestStatusIndicator
                status={
                    getValue() === PayerRequestStatusIndicator.Statuses.RESOLVED
                        ? PayerRequestStatusIndicator.Statuses.MONITORING
                        : getValue()
                }
            />
        ),
    },
];

export const RequestArchiveGrid = ({ pbmOnlyAccess = false }: RequestArchiveGridProps) => {
    const navigate = useNavigate();
    const { payerId, permissions, segmentIds } = useAppSelector((state) => state.auth);
    const { segmentId } = useAppSelector((state) => state.views);

    const {
        sort,
        search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort,
        updateGridSearch,
        updateGridActiveFilters,
        updateFilterPaneWidth,
        updateCurrentPage,
    } = useGridState(RequestType.ARCHIVE, initialSortProperties);
    const apiQueryParams =
        payerId && activeFilters
            ? {
                  page,
                  payerId,
                  segmentIds: segmentId ? [segmentId] : segmentIds,
                  searchText: search,
                  timeframe: activeFilters.completed_date?.[0] as TimeFrameOption,
                  requestType:
                      activeFilters.request_type as (typeof RequestType)[keyof typeof RequestType][],
                  requestSubType: activeFilters.request_sub_type,
                  issueSource: activeFilters.issue_source,
                  requestStatus:
                      activeFilters.request_status as (typeof PayerRequestStatusIndicator.Statuses)[keyof typeof PayerRequestStatusIndicator.Statuses][],
                  urgent: activeFilters.urgent?.[0] as never as boolean,
                  pbmOnly: activeFilters.pbm_only?.[0] as never as boolean,
                  sort,
              }
            : undefined;
    const { data: firstPageData, isFetching: isFirstPageDataFetching } =
        useGetArchivedRequestsQuery(apiQueryParams ? { ...apiQueryParams, page: 0 } : skipToken);
    const { data: currentPageData, isFetching: isCurrentPageDataFetching } =
        useGetArchivedRequestsQuery(apiQueryParams ?? skipToken);

    const userNameMap = useUserNameMap({ payerId: payerId! });
    const requests = useMemo(
        () =>
            currentPageData?.requests?.map((request) => ({
                ...request,
                submitter_full_name: userNameMap?.[request.submitter_upn],
            })),
        [currentPageData?.requests, userNameMap],
    );

    return (
        <GridWithFilters
            headerContent={
                (firstPageData?.filteredCount ?? 0) > 0 ? (
                    <div className="margin__bottom--medium">
                        <InlineMessage
                            intent={InlineMessage.Intent.INFO}
                            label={`The ${firstPageData!.count} requests viewable below do not include ${firstPageData!.filteredCount} restricted requests hidden from view.`}
                        />
                    </div>
                ) : undefined
            }
            viewsPaneContent={<ViewsControls />}
            data={requests}
            isLoading={isFirstPageDataFetching || isCurrentPageDataFetching}
            columns={columns}
            sortProperties={sort}
            onSort={updateGridSort}
            enableSearch
            searchText={search}
            onSearch={updateGridSearch}
            timeFrameConfig={timeFrameConfig}
            filterCategories={filterCategories(permissions, pbmOnlyAccess)}
            activeFilters={activeFilters}
            onFilter={updateGridActiveFilters}
            filterPaneRenderWidth={filterPaneWidth}
            onFilterPaneResize={updateFilterPaneWidth}
            currentPage={page}
            onPageChange={updateCurrentPage}
            totalCount={firstPageData?.count}
            onRowClick={(rowData) =>
                navigate(AppRoutes.requestDetails(rowData.request_id), {
                    state: {
                        backToDestination: {
                            text: getActionButtonPageTitleLabel({
                                requestType: RequestType.ARCHIVE,
                                requestStatus: rowData.request_status,
                            }),
                        },
                    },
                })
            }
        />
    );
};
